import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, ExternalLink } from "lucide-react";
import { useGet } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Pill, Loading } from "@/components/app/Bits";
import { Button } from "@/components/ui/button";

const DONE = ["done", "acknowledged", "approved", "skipped", "waived"];
export const STATUS = { open: "To do", submitted: "Waiting for approval", done: "Done", acknowledged: "Acknowledged", approved: "Approved", skipped: "Skipped", waived: "Waived" };
const ACTION = { complete: "Mark done", acknowledge: "I've read this", approval: "Submit for approval" };

function Step({ s, onDo }) {
  const done = DONE.includes(s.status);
  return (
    <li className="p-4 flex gap-3" data-testid={`onb-step-${s.id}`}>
      {done ? <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" /> : <Circle className="h-5 w-5 text-stone-300 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{s.title}</span><Pill tone={done ? "green" : s.overdue ? "red" : "stone"}>{s.overdue ? "Overdue" : STATUS[s.status]}</Pill>{s.type === "walkthrough" && <Pill>86'd tour</Pill>}</div>
        {s.body && <p className="text-sm text-stone-600 mt-1">{s.body}</p>}
        <div className="text-xs text-stone-500 mt-1 flex flex-wrap gap-3">{s.due && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />Due {s.due}</span>}{s.trainer_name && <span>Trainer: {s.trainer_name}</span>}{s.record && <span>{STATUS[s.record.status]} by {s.record.by_name} · {s.record.at?.slice(0, 10)}</span>}</div>
        <div className="flex flex-wrap gap-2 mt-2">
          {s.path && <Link to={s.path} className="text-sm underline" data-testid={`onb-open-${s.id}`}>Open {s.title}</Link>}
          {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm underline inline-flex items-center gap-1">Open link<ExternalLink className="h-3 w-3" /></a>}
          {s.checklist_id && <Link to="/app/me/checklists" className="text-sm underline">Open checklists</Link>}
          {!done && s.status !== "submitted" && <Button className="h-9" onClick={() => onDo(s)} data-testid={`onb-do-${s.id}`}>{ACTION[s.requirement]}</Button>}
        </div>
      </div>
    </li>
  );
}

export function OnboardingCard({ a, refetch }) {
  const doStep = async (s) => { try { await api.post(`/onboarding/mine/${a.id}/steps/${s.id}`, {}); toast.success("Saved"); refetch(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card-surface overflow-hidden" data-testid={`onb-assignment-${a.id}`}>
      <div className="p-4 border-b">
        <div className="flex flex-wrap items-center gap-2"><span className="font-display text-xl">{a.flow_name}</span><Pill tone={a.primary ? "dark" : "stone"}>{a.primary ? "Primary role" : a.role}</Pill><Pill tone={a.required ? "amber" : "stone"}>{a.required ? "Required" : "Optional"}</Pill></div>
        {a.intro && <p className="text-sm text-stone-600 mt-1">{a.intro}</p>}
        <div className="flex items-center gap-3 mt-3"><div className="flex-1 h-2 rounded-full bg-[#F3EFE6] overflow-hidden"><div className="h-full bg-stone-900" style={{ width: `${a.pct}%` }} /></div><span className="text-xs num" data-testid={`onb-progress-${a.id}`}>{a.done}/{a.total}</span></div>
        <div className="text-sm mt-2" data-testid={`onb-next-${a.id}`}>{a.next_step ? <>Next: <b>{a.next_step.title}</b></> : "All steps complete"}</div>
      </div>
      <ol className="divide-y">{a.steps.map((s) => <Step key={s.id} s={s} onDo={doStep} />)}</ol>
    </div>
  );
}

export function MyOnboarding({ compact = false }) {
  const { data, isLoading, refetch } = useGet("/onboarding/mine");
  if (isLoading) return compact ? null : <Loading />;
  const items = data?.items || [];
  if (compact) {
    const a = items.find((x) => x.pct < 100);
    return a ? <Link to="/app/me/onboarding" className="card-surface p-4 block hover:bg-white mt-4" data-testid="home-onboarding-card"><div className="text-xs text-stone-500">Onboarding · {a.done}/{a.total} done</div><div className="font-medium">{a.next_step ? `Next: ${a.next_step.title}` : a.flow_name}</div></Link> : null;
  }
  return (
    <div className="space-y-4" data-testid="my-onboarding">
      <h1 className="font-display text-4xl">My onboarding</h1>
      {!items.length && <div className="card-surface p-4 text-sm text-stone-500" data-testid="my-onboarding-empty">No onboarding assigned to you.</div>}
      {items.map((a) => <OnboardingCard key={a.id} a={a} refetch={refetch} />)}
    </div>
  );
}
